"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.correctionRegisterResponseSchema = exports.correctionRegisterRowSchema = exports.correctionRegisterParameterEnum = exports.correctionReceiptLookupSchema = exports.correctionReceiptSchema = void 0;
const zod_1 = require("zod");
const financial_payment_correction_request_1 = require("../request/financial-payment-correction.request");
const financial_correction_stage_response_1 = require("./financial-correction-stage.response");
/**
 * The receipt a clerk typed a number for, as the Correction tab needs it to offer the right choices — the same facts the
 * Bill Register's View used to hand the correction form, now resolved on the server from the number alone.
 *
 * It is returned ONLY for a receipt the caller created (see `correctionReceiptLookupSchema`).
 */
exports.correctionReceiptSchema = zod_1.z.object({
    /** OP_BILL: an OP receipt or a walk-in receipt (a `FinInvoice`). IP_ADVANCE: an advance (an `IpAdmissionDeposit`). */
    kind: zod_1.z.enum(['OP_BILL', 'IP_ADVANCE']),
    receiptNumber: zod_1.z.string(),
    /** ISO instant the receipt was raised. */
    receiptDate: zod_1.z.string(),
    amountPaise: zod_1.z.number().int(),
    patientName: zod_1.z.string().nullable(),
    /** The patient's UHID. Null on a walk-in receipt. */
    opNumber: zod_1.z.string().nullable(),
    createdByName: zod_1.z.string().nullable(),
    /** OP_BILL: `FinInvoice.id`. Null for an advance. */
    invoiceId: zod_1.z.string().uuid().nullable(),
    walkIn: zod_1.z.boolean(),
    walkInNumber: zod_1.z.string().nullable(),
    /** The bill carries a CONS FEE line — only then is "Doctor transfer" on offer. */
    hasConsultation: zod_1.z.boolean(),
    /** The consultant now (a USER id, like `Appointment.doctorId`) and their name. */
    doctorId: zod_1.z.string().uuid().nullable(),
    doctorName: zod_1.z.string().nullable(),
    /** IP_ADVANCE: `IpAdmissionDeposit.id`. Null for an OP receipt. */
    depositId: zod_1.z.string().uuid().nullable(),
    billNumber: zod_1.z.string().nullable(),
    admissionNumber: zod_1.z.string().nullable(),
    /** The ONE payment line a payment-method correction aims at. Null on a split payment, an unpaid bill or a waived one. */
    payment: zod_1.z
        .object({
        paymentLineId: zod_1.z.string().uuid(),
        paymentId: zod_1.z.string().uuid().nullable(),
        mode: zod_1.z.string(),
        referenceNumber: zod_1.z.string().nullable(),
        amountPaise: zod_1.z.number().int(),
    })
        .nullable(),
});
/**
 * FOUND      the receipt exists and the caller created it — `receipt` is filled;
 * NOT_FOUND  no receipt carries that number;
 * NOT_YOURS  it exists but another user created it — `message` says who, and `receipt` is NULL so nothing about the
 *            receipt (patient, amount) is disclosed to someone who may not correct it.
 */
exports.correctionReceiptLookupSchema = zod_1.z.object({
    result: zod_1.z.enum(['FOUND', 'NOT_FOUND', 'NOT_YOURS']),
    message: zod_1.z.string().nullable(),
    receipt: exports.correctionReceiptSchema.nullable(),
});
/** What was asked to be corrected, in the words the desk uses. */
exports.correctionRegisterParameterEnum = zod_1.z.enum(['PAYMENT_METHOD', 'OP_NUMBER', 'DOCTOR_TRANSFER', 'BILL_NUMBER', 'PATIENT_NAME']);
/** One line of the register: a request the caller entered, from either request table, in one shape. */
exports.correctionRegisterRowSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    parameter: exports.correctionRegisterParameterEnum,
    /** ISO instant the request was entered. */
    requestedAt: zod_1.z.string(),
    receiptNumber: zod_1.z.string().nullable(),
    opNumber: zod_1.z.string().nullable(),
    patientName: zod_1.z.string().nullable(),
    amountPaise: zod_1.z.number().int(),
    /** The value the receipt carries now, and the one asked for — one readable string each (a mode + reference, an OP number, a doctor ...). */
    fromValue: zod_1.z.string().nullable(),
    toValue: zod_1.z.string().nullable(),
    reason: zod_1.z.string(),
    status: financial_payment_correction_request_1.paymentCorrectionStatusEnum,
    stage: financial_correction_stage_response_1.correctionStageEnum,
    verifiedByName: zod_1.z.string().nullable(),
    verifiedAt: zod_1.z.string().nullable(),
    approvedByName: zod_1.z.string().nullable(),
    approvedAt: zod_1.z.string().nullable(),
    /** The verifier's / approver's reason, when the request was rejected or noted. */
    decisionReason: zod_1.z.string().nullable(),
});
exports.correctionRegisterResponseSchema = zod_1.z.object({
    rows: zod_1.z.array(exports.correctionRegisterRowSchema),
    /** More rows matched than were returned — narrow the dates. */
    truncated: zod_1.z.boolean(),
});
//# sourceMappingURL=financial-correction-register.response.js.map